/**
 * Enterprise Logger Utility
 * 
 * Provides structured logging with context awareness, performance monitoring,
 * and environment-based gating for clean production console output.
 */

// Log levels in order of severity
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

// Context interface for structured logging
export interface LogContext {
  userId?: string;
  sessionId?: string;
  component?: string;
  feature?: string;
  operation?: string;
  duration?: number;
  metadata?: Record<string, unknown>;
}

// Logger configuration
interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  enablePerformanceTracking: boolean;
  enableContextual: boolean;
  productionSilent: boolean;
}

// Performance tracking for operations
interface PerformanceTracker {
  [key: string]: {
    startTime: number;
    count: number;
    totalTime: number;
    avgTime: number;
  };
}

class Logger {
  private config: LoggerConfig;
  private performanceTrackers: PerformanceTracker = {};
  private contextStack: LogContext[] = [];

  constructor() {
    const isDevelopment = import.meta.env.DEV;
    const isDebugEnabled = import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true';

    this.config = {
      level: isDevelopment ? LogLevel.ERROR : LogLevel.ERROR, // Only show errors and critical issues
      enableConsole: isDevelopment || isDebugEnabled,
      enablePerformanceTracking: false, // Disable performance tracking logs
      enableContextual: false, // Disable context to reduce clutter
      productionSilent: !isDevelopment && !isDebugEnabled
    };
  }

  // Context management
  pushContext(context: LogContext): void {
    this.contextStack.push(context);
  }

  popContext(): LogContext | undefined {
    return this.contextStack.pop();
  }

  private getCurrentContext(): LogContext {
    return this.contextStack.length > 0 
      ? this.contextStack[this.contextStack.length - 1] 
      : {};
  }

  // Core logging methods
  private log(level: LogLevel, message: string, context?: LogContext, error?: Error): void {
    // Skip if level is below configured threshold
    if (level < this.config.level) {
return;
}
    
    // Skip all logging in production unless explicitly enabled
    if (this.config.productionSilent) {
return;
}

    const timestamp = new Date().toISOString();
    const currentContext = this.getCurrentContext();
    const fullContext = { ...currentContext, ...context };
    
    const logEntry = {
      timestamp,
      level: LogLevel[level],
      message,
      context: this.config.enableContextual ? fullContext : undefined,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : undefined
    };

    // Output to console if enabled
    if (this.config.enableConsole) {
      this.outputToConsole(level, logEntry);
    }

    // Could extend to send to external logging service here
    // this.sendToExternalService(logEntry);
  }

  private outputToConsole(level: LogLevel, logEntry: unknown): void {
    const { timestamp, message, context, error } = logEntry;
    const contextStr = context && Object.keys(context).length > 0 
      ? ` [${this.formatContext(context)}]` 
      : '';
    
    const logMessage = `${timestamp} ${message}${contextStr}`;

    switch (level) {
      case LogLevel.DEBUG:
        console.debug('🔍', logMessage, error || '');
        break;
      case LogLevel.INFO:
        console.info('ℹ️', logMessage, error || '');
        break;
      case LogLevel.WARN:
        console.warn('⚠️', logMessage, error || '');
        break;
      case LogLevel.ERROR:
      case LogLevel.CRITICAL:
        console.error('🚨', logMessage, error || '');
        break;
    }
  }

  private formatContext(context: LogContext): string {
    const parts: string[] = [];
    
    if (context.component) {
parts.push(`${context.component}`);
}
    if (context.feature) {
parts.push(`${context.feature}`);
}
    if (context.operation) {
parts.push(`${context.operation}`);
}
    if (context.userId) {
parts.push(`user:${context.userId.slice(-6)}`);
}
    if (context.duration) {
parts.push(`${context.duration}ms`);
}
    
    return parts.join(' | ');
  }

  // Public logging methods
  debug(message: string, context?: LogContext): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log(LogLevel.INFO, message, context);
  }

  warn(message: string, context?: LogContext, error?: Error): void {
    this.log(LogLevel.WARN, message, context, error);
  }

  error(message: string, context?: LogContext, error?: Error): void {
    this.log(LogLevel.ERROR, message, context, error);
  }

  critical(message: string, context?: LogContext, error?: Error): void {
    this.log(LogLevel.CRITICAL, message, context, error);
  }

  // Performance tracking
  startTimer(operation: string): string {
    if (!this.config.enablePerformanceTracking) {
return operation;
}
    
    const timerId = `${operation}_${Date.now()}_${Math.random()}`;
    
    if (!this.performanceTrackers[operation]) {
      this.performanceTrackers[operation] = {
        startTime: 0,
        count: 0,
        totalTime: 0,
        avgTime: 0
      };
    }

    this.performanceTrackers[timerId] = {
      startTime: performance.now(),
      count: 1,
      totalTime: 0,
      avgTime: 0
    };

    return timerId;
  }

  endTimer(timerId: string, context?: LogContext): number {
    if (!this.config.enablePerformanceTracking) {
return 0;
}
    
    const tracker = this.performanceTrackers[timerId];
    if (!tracker) {
      this.warn('Timer not found', { operation: timerId });
      return 0;
    }

    const duration = performance.now() - tracker.startTime;
    const operation = timerId.split('_')[0];
    
    // Update operation statistics
    const opTracker = this.performanceTrackers[operation];
    if (opTracker) {
      opTracker.count++;
      opTracker.totalTime += duration;
      opTracker.avgTime = opTracker.totalTime / opTracker.count;
    }

    // Log performance if it's slow
    if (duration > 1000) {
      this.warn(`Slow operation detected: ${operation}`, {
        ...context,
        operation,
        duration: Math.round(duration)
      });
    } else if (duration > 100) {
      this.debug(`Operation completed: ${operation}`, {
        ...context,
        operation,
        duration: Math.round(duration)
      });
    }

    // Clean up timer
    delete this.performanceTrackers[timerId];
    
    return duration;
  }

  // Performance reporting
  getPerformanceStats(): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(this.performanceTrackers)
        .filter(([key]) => !key.includes('_')) // Only operation summaries
        .map(([operation, stats]) => [operation, {
          count: stats.count,
          avgTime: Math.round(stats.avgTime),
          totalTime: Math.round(stats.totalTime)
        }])
    );
  }

  // Component-specific logger factory
  createComponentLogger(component: string, feature?: string) {
    return {
      debug: (message: string, context?: LogContext) => 
        this.debug(message, { component, feature, ...context }),
      info: (message: string, context?: LogContext) => 
        this.info(message, { component, feature, ...context }),
      warn: (message: string, context?: LogContext, error?: Error) => 
        this.warn(message, { component, feature, ...context }, error),
      error: (message: string, context?: LogContext, error?: Error) => 
        this.error(message, { component, feature, ...context }, error),
      critical: (message: string, context?: LogContext, error?: Error) => 
        this.critical(message, { component, feature, ...context }, error),
      startTimer: (operation: string) => 
        this.startTimer(`${component}.${operation}`),
      endTimer: this.endTimer.bind(this)
    };
  }

  // Configuration updates
  updateConfig(updates: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...updates };
  }

  // Emergency override for critical debugging
  enableEmergencyLogging(): void {
    this.config.enableConsole = true;
    this.config.level = LogLevel.DEBUG;
    this.config.productionSilent = false;
    this.warn('Emergency logging enabled - disable after debugging');
  }
}

// Singleton instance
export const logger = new Logger();

// Convenience exports for direct usage
export const createComponentLogger = logger.createComponentLogger.bind(logger);
export const startTimer = logger.startTimer.bind(logger);
export const endTimer = logger.endTimer.bind(logger);
export const getPerformanceStats = logger.getPerformanceStats.bind(logger);

// Default export
export default logger;